import {
  useIntegrationMetrics,
  useRunReconciliation,
} from '../hooks/use-integration-reconciliation';

import { fmtDateTime } from './reconciliation-labels';

export interface ReconciliationPanelProps {
  canManage: boolean;
  /** Abre o MismatchDrawer (ancorado no ErpsCrmsTab). */
  onOpenMismatches: () => void;
}

/**
 * A4 REVAMP — KPI strip de reconciliation + botão "Reconciliar agora" (Brief 5
 * Fatia 3a · #314). KPIs reais de `GET /metrics`; "Reconciliar agora" dispara
 * `POST /reconcile` (gated `integrations-manage`, defense-in-depth — backend nega
 * sem o capability). O KPI de mismatches abre o MismatchDrawer.
 *
 * Honest-defer (balde 3b): "Webhooks 24h" não tem ingestão hoje → exibido como
 * "Em breve", sem fabricar número.
 */
export function ReconciliationPanel({
  canManage,
  onOpenMismatches,
}: ReconciliationPanelProps): JSX.Element | null {
  const { data: metrics, isLoading, error } = useIntegrationMetrics();
  const reconcile = useRunReconciliation();

  if (isLoading) {
    return <p className="text-sm text-text-secondary">Carregando métricas de reconciliation…</p>;
  }
  if (error != null || metrics === undefined) {
    return (
      <p className="text-sm text-red-600">
        Falha ao carregar métricas{error != null ? `: ${error.message}` : ''}.
      </p>
    );
  }

  const activeCount = metrics.connectors.filter((c) => c.status === 'active').length;
  const hasMismatches = metrics.pendingMismatches > 0;

  return (
    <section className="flex flex-col gap-3" aria-label="Reconciliação">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-text-primary">Reconciliação</h2>
        {canManage && (
          <button
            type="button"
            onClick={() => reconcile.mutate()}
            disabled={reconcile.isPending}
            className="rounded bg-accent px-4 py-2 text-sm text-white hover:bg-accent-hover disabled:opacity-50"
          >
            {reconcile.isPending ? 'Reconciliando…' : 'Reconciliar agora'}
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi
          label="Conectores ativos"
          value={`${activeCount}`}
          hint={`de ${metrics.connectors.length} no tenant`}
        />
        <Kpi
          label="Última reconciliação"
          value={
            metrics.lastReconciliationAt !== null
              ? fmtDateTime(metrics.lastReconciliationAt)
              : 'Nunca'
          }
          hint={
            metrics.lastReconciliationAt !== null
              ? 'execução mais recente'
              : 'rode uma reconciliação'
          }
        />
        <button
          type="button"
          onClick={onOpenMismatches}
          className="flex flex-col items-start rounded-lg border border-border-subtle bg-surface px-4 py-3 text-left hover:bg-bg-muted"
        >
          <span className="text-xs text-text-secondary">Divergências pendentes</span>
          <span
            className={`mt-1 text-2xl font-semibold ${hasMismatches ? 'text-amber-600' : 'text-text-primary'}`}
          >
            {metrics.pendingMismatches}
          </span>
          <span className="mt-0.5 text-xs text-accent">Ver divergências →</span>
        </button>
        <Kpi label="Webhooks 24h" value="Em breve" hint="ingestão em desenvolvimento" muted />
      </div>

      {reconcile.isSuccess && (
        <p className="text-xs text-text-secondary" role="status">
          Reconciliação concluída · {reconcile.data.connectorsChecked} conector(es) ·{' '}
          {reconcile.data.flagsCreated} divergência(s) nova(s).
        </p>
      )}
      {reconcile.error != null && (
        <p className="text-xs text-red-600">Falha ao reconciliar: {reconcile.error.message}</p>
      )}
    </section>
  );
}

interface KpiProps {
  label: string;
  value: string;
  hint: string;
  muted?: boolean;
}

function Kpi({ label, value, hint, muted = false }: KpiProps): JSX.Element {
  return (
    <div className="flex flex-col rounded-lg border border-border-subtle bg-surface px-4 py-3">
      <span className="text-xs text-text-secondary">{label}</span>
      <span
        className={`mt-1 text-2xl font-semibold ${muted ? 'text-text-secondary' : 'text-text-primary'}`}
      >
        {value}
      </span>
      <span className="mt-0.5 text-xs text-text-secondary">{hint}</span>
    </div>
  );
}
